import { Component, computed, inject, linkedSignal, signal } from '@angular/core';
import { OrganizationsStore } from '../../../organizations/store/organizations.store';
import { toModel, toPayload } from './definitions';
import { disabled, form, FormField, required } from '@angular/forms/signals';
import {
  InputError,
  Button,
  InputLabel,
  FileInput,
  InputSimple,
  Select,
  ConfirmService,
  Toggle,
  ToggleLabel,
  Badge,
} from 'excellence-ui';
import { EditFormSkeleton } from '../../../../shared/components/edit-form-skeleton/edit-form-skeleton';
import { NgIcon } from '@ng-icons/core';
import { FieldErrors } from '../../../../shared/components/field-errors/field-errors';
import { UserInfoService } from '../../../user/services/user-info.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { HasRoleDirective } from '../../../../shared/directives/has-role';
import { HttpError, PersonalConsultant } from '../../../../core/clients/omni-core/models';
import { Router } from '@angular/router';
import { HttpErrorComponent } from '../../../../shared/components/http-error/http-error';
import { environment } from '../../../../../environments/environment';
import { DatePipe } from '@angular/common';

@Component({
  selector: 'app-settings-general-page',
  imports: [
    InputError,
    EditFormSkeleton,
    Button,
    NgIcon,
    InputLabel,
    FileInput,
    InputSimple,
    FormField,
    FieldErrors,
    TranslocoPipe,
    Select,
    HasRoleDirective,
    HttpErrorComponent,
    Toggle,
    ToggleLabel,
    DatePipe,
    Badge,
  ],
  templateUrl: './settings-general-page.html',
})
export class SettingsGeneralPage {
  private organizationsStore = inject(OrganizationsStore);
  private userInfoService = inject(UserInfoService);
  private translocoService = inject(TranslocoService);
  private confirmService = inject(ConfirmService);
  private router = inject(Router);

  protected personalConsultantOptions = (
    Object.keys(environment.personalConsultants) as PersonalConsultant[]
  ).map((key) => ({
    displayValue: environment.personalConsultants[key]?.name,
    value: key,
  }));

  private isAdmin = computed(() => this.userInfoService.info()?.role === 'USER_ADMIN'); // todo: check org role for USER_BASIC
  protected state = this.organizationsStore.selected;
  private isProfilePicUploadLoading = signal(false);
  private profilePicUploadError = signal<HttpError | null | unknown>(null);

  protected data = linkedSignal(() => toModel(this.state()?.data));
  protected metadata = linkedSignal(() => this.state()?.data?.metadata);

  protected form = form(this.data, (schemaPath) => {
    required(schemaPath.name);
    disabled(schemaPath.name, () => !this.isAdmin());

    required(schemaPath.industry);

    required(schemaPath.personalConsultant);

    required(schemaPath.legalName, {
      when: ({ valueOf }) => valueOf(schemaPath.hasDifferentLegalName) === true,
    });
  });
  protected showValidationErrors = signal(false);

  protected isUpdateLoading = computed(
    () => this.state()?.isEditLoading || this.isProfilePicUploadLoading(),
  );
  protected updateError = computed(() => this.state()?.editError || this.profilePicUploadError());
  protected isDeleteLoading = computed(() => !!this.state()?.isDeleteLoading);
  protected deleteError = computed(() => this.state()?.deleteError);

  private markAllAsTouched() {
    this.form.industry().markAsTouched();
    this.form.name().markAsTouched();
    this.form.profilePicture().markAsTouched();
    this.form.size().markAsTouched();
    this.form.personalConsultant().markAsTouched();
    this.form.legalName().markAsTouched();
  }

  protected async onSubmit() {
    if (!this.form().valid()) {
      this.markAllAsTouched();
      this.showValidationErrors.set(true);
      return;
    }
    const orgaID = this.state()!.id;
    await this.organizationsStore.update(orgaID, toPayload(this.form().value()));
    this.form().reset();
  }

  protected async onClickDelete() {
    const confirm = await this.confirmService.confirm({
      allowCancel: true,
      cancelButtonText: this.translocoService.translate(
        'cockpit.settings.general.deleteConfirm.cancelButton',
      ),
      confirmButtonText: this.translocoService.translate(
        'cockpit.settings.general.deleteConfirm.confirmButton',
      ),
      mode: 'destructive',
      title: this.translocoService.translate('cockpit.settings.general.deleteConfirm.title'),
      message: this.translocoService.translate('cockpit.settings.general.deleteConfirm.message'),
      icon: 'heroExclamationCircle',
    });
    if (!confirm) {
      return;
    }
    await this.organizationsStore.delete(this.state()!.id);
    this.router.navigate(['/organizations']);
  }
}
